import { z } from "zod";
import { cursorQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { actorSchema, actorTypeSchema, anyEntityTypeSchema } from "./common.ts";

/**
 * Audit log + per-record activity timeline.
 * Every mutation writes its audit row in the SAME transaction as the change and
 * its outbox event; audit rows are append-only (no UPDATE/DELETE grants).
 */

/** "<entity>.<verb>" (e.g. "client.updated", "invoice.payment_recorded", "auth.login_failed"). */
export const AUDIT_ACTION_REGEX = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
export const auditActionSchema = z.string().max(80).regex(AUDIT_ACTION_REGEX, "Use <entity>.<verb>");

/** Conventional verbs (not exhaustive; modules may add their own). */
export const AUDIT_VERBS = [
  "created", "updated", "deleted", "archived", "restored", "status_changed", "assigned", "commented",
  "sent", "viewed", "accepted", "declined", "signed", "paid", "refunded", "imported", "exported",
  "invited", "joined", "removed", "role_changed", "login", "login_failed", "logout",
] as const;

/** Stored in place of secret values in `changes`. */
export const AUDIT_REDACTED = "[redacted]";
/** Keys whose values never reach the audit log (matched case-insensitively, at any depth). */
export const AUDIT_SENSITIVE_KEY_REGEX = /(password|secret|token|hash|otp|totp|apikey|api_key|accountnumber|account_number|cvv|pin)$/i;
/** Keys ignored when diffing. */
export const AUDIT_IGNORED_KEYS = ["createdAt", "updatedAt", "version", "orgId", "searchVector"] as const;

export const auditChangeSchema = z.object({ from: z.unknown(), to: z.unknown() });
export const auditChangesSchema = z.record(z.string(), auditChangeSchema);
export type AuditChanges = z.infer<typeof auditChangesSchema>;

/** Audit / activity event DTO. `entityId` is null for org-level events. */
export const auditEventSchema = z.object({
  id: uuidSchema,
  action: z.string(),
  entityType: anyEntityTypeSchema,
  entityId: uuidSchema.nullable(),
  /** Human sentence, e.g. "Priya changed the status of Acme Corp to Active". */
  summary: z.string(),
  changes: auditChangesSchema,
  actor: actorSchema,
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
  createdAt: timestampSchema,
});
export type AuditEventDto = z.infer<typeof auditEventSchema>;

/**
 * GET /api/v1/audit (perm `audit:view`) → CursorPage<AuditEventDto>, newest first.
 * `action` is exact, or a prefix when it ends with "*" ("invoice.*").
 * `q` searches summary text. `from`/`to` bound `createdAt` (inclusive/exclusive).
 */
export const listAuditQuerySchema = cursorQuerySchema
  .extend({
    entityType: anyEntityTypeSchema.optional(),
    entityId: uuidSchema.optional(),
    actorId: uuidSchema.optional(),
    actorType: actorTypeSchema.optional(),
    action: z
      .string()
      .trim()
      .max(80)
      .regex(/^[a-z0-9_.]+\*?$/, "Use an action or a prefix ending in *")
      .optional(),
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
    q: z.string().trim().max(200).optional(),
  })
  .refine((o) => !o.from || !o.to || Date.parse(o.from) <= Date.parse(o.to), { message: "from must be before to", path: ["to"] });
export type ListAuditQuery = z.infer<typeof listAuditQuerySchema>;

/**
 * GET /api/v1/activity/:entityType/:entityId?cursor&limit → CursorPage<AuditEventDto>.
 * The record timeline: events on the record itself plus child records its module
 * links to it (e.g. a client's contacts, comments and files). Requires read access
 * to the record (see ENTITY_READ_PERMISSION); unreadable → 404.
 */
export const activityParamsSchema = z.object({
  entityType: anyEntityTypeSchema,
  entityId: uuidSchema,
});
export const activityQuerySchema = cursorQuerySchema;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function auditAction(entityType: string, verb: string): string {
  return `${entityType}.${verb}`;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date);
}

/** Deep-replaces values of sensitive keys with AUDIT_REDACTED; Dates → ISO strings. */
export function redactSensitive(value: unknown): unknown {
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(redactSensitive);
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = AUDIT_SENSITIVE_KEY_REGEX.test(k) ? AUDIT_REDACTED : redactSensitive(v);
    return out;
  }
  return value;
}

function stableJson(v: unknown): string {
  return JSON.stringify(v, (_k, val: unknown) =>
    isPlainObject(val)
      ? Object.keys(val)
          .sort()
          .reduce<Record<string, unknown>>((acc, key) => {
            acc[key] = val[key];
            return acc;
          }, {})
      : val,
  );
}

/**
 * Field-level diff for audit `changes` (top-level keys; nested objects compared
 * structurally and recorded whole). Sensitive keys are redacted, ignored keys
 * skipped, `undefined` treated as null.
 */
export function diffChanges(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  opts: { ignore?: readonly string[] } = {},
): AuditChanges {
  const ignore = new Set<string>([...AUDIT_IGNORED_KEYS, ...(opts.ignore ?? [])]);
  const a = before ?? {};
  const b = after ?? {};
  const changes: AuditChanges = {};
  for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (ignore.has(key)) continue;
    const from = redactSensitive(a[key] ?? null);
    const to = redactSensitive(b[key] ?? null);
    if (AUDIT_SENSITIVE_KEY_REGEX.test(key)) {
      if (stableJson(a[key] ?? null) !== stableJson(b[key] ?? null)) changes[key] = { from: AUDIT_REDACTED, to: AUDIT_REDACTED };
      continue;
    }
    if (stableJson(from) !== stableJson(to)) changes[key] = { from, to };
  }
  return changes;
}

/**
 * The verdict of a tamper-evidence check.
 *
 * `unsealed` counts rows written before the chain existed. They are reported
 * rather than failed: calling honest history a breach is how a verifier gets
 * switched off and stops being read.
 */
export const auditChainBreakSchema = z.object({
  rowId: z.string(),
  createdAt: z.string(),
  reason: z.enum(["content_changed", "predecessor_missing"]),
  detail: z.string(),
});

export const auditVerifySchema = z.object({
  ok: z.boolean(),
  checked: z.number().int(),
  unsealed: z.number().int(),
  headHash: z.string().nullable(),
  /** Short form of the head, to compare against a value recorded elsewhere. */
  digest: z.string(),
  breaks: z.array(auditChainBreakSchema),
});
export type AuditVerifyResult = z.output<typeof auditVerifySchema>;
